import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { selectError, selectLoading, selectMessageLoading, selectMessages, selectToken } from '../../store/selectors/ai-chat.selectors';
import { CloseChatSession, CreateChatSession, SendMessage } from '../../store/actions/ai-chat.actions';
import { ChatMessage } from '../../interfaces/chat-message.interface';
// import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
  selector: 'app-ai-chat-dialog',
  templateUrl: './ai-chat-dialog.component.html',
  styleUrls: ['./ai-chat-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AiChatDialogComponent implements OnInit, OnDestroy, AfterContentChecked {
  // @ViewChild('autosize') autosize!: CdkTextareaAutosize;

  public chatOpened: boolean = false;
  public messageTextArea = new FormControl('');
  public isMaximized: boolean = false;
  public readonly loading$: Observable<boolean>;
  public readonly messageLoading$: Observable<boolean>;
  public readonly error$: Observable<Error | null>;
  public readonly messages$: Observable<ChatMessage[]>;
  public retryCallbackFn = (_args: null): void => {
    this.store.dispatch(CreateChatSession());
  };

  private backdrop: HTMLElement | null = null;
  private token: string | null = null;
  private readonly token$: Observable<string | null>;
  public readonly destroyed$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AiChatDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { userName: string },
    private cdr: ChangeDetectorRef,
    private store: Store
  ) {
    this.loading$ = this.store.select(selectLoading);
    this.messageLoading$ = this.store.select(selectMessageLoading);
    this.error$ = this.store.select(selectError);
    this.token$ = this.store.select(selectToken);
    this.messages$ = this.store.select(selectMessages);
  }

  ngOnInit(): void {
    // Create backdrop element if it doesn't exist
    this.createBackdrop();

    this.token$.pipe(takeUntil(this.destroyed$)).subscribe((token) => {
      this.token = token;
    });

    this.messageLoading$.pipe(takeUntil(this.destroyed$)).subscribe((messageLoading) => {
      if (messageLoading) {
        this.messageTextArea.disable();
      } else {
        this.messageTextArea.enable();
      }
    });
  }

  ngAfterContentChecked() {
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    // Remove backdrop element
    if (this.backdrop && this.backdrop.parentNode) {
      this.backdrop.parentNode.removeChild(this.backdrop);
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  public onChatBubbleClick(): void {
    if (!this.token) {
      this.store.dispatch(CreateChatSession());
    }
    this.chatOpened = true;

    // Enable backdrop
    this.dialogRef.disableClose = false;
    this.dialogRef.updateSize(this.isMaximized ? '50%' : '400px', this.isMaximized ? '80%' : '700px');

    // Re-enable and force recalculation after animation
    this.dialogRef.addPanelClass('ai-chat-dialog-expanded');
    this.showBackdrop();
  }

  public minimizeChat(): void {
    this.chatOpened = false;
    this.dialogRef.updateSize('58px', '58px');
    this.dialogRef.removePanelClass('ai-chat-dialog-expanded');
    this.dialogRef.disableClose = true;
    this.hideBackdrop();
    this.cdr.markForCheck();
  }

  public sendMessage(): void {
    // Implement send message logic
    const value = this.messageTextArea.value;

    if (value) {
      // this.userMessages.push({ message: value, timestamp: new Date() });
      const message: ChatMessage = {
        message: value,
        timestamp: new Date(),
        type: 'user',
      };
      this.store.dispatch(SendMessage({ message }));
    }
    // Reset the message input
    this.messageTextArea.setValue('');
    this.messageTextArea.reset();
    this.cdr.markForCheck();
  }

  public toggleChatSize(): void {
    // console.log('Toggling chat size');
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      this.dialogRef.updateSize('50%', '80%');
    } else {
      this.dialogRef.updateSize('400px', '700px');
    }
    this.cdr.markForCheck();
  }

  public closeSession(isReset: boolean): void {
    this.store.dispatch(CloseChatSession({ isReset }));
    if (!isReset) {
      this.minimizeChat();
    }
  }

  private createBackdrop(): void {
    // Create a custom backdrop element
    this.backdrop = document.createElement('div');
    this.backdrop.classList.add('ai-chat-backdrop', 'cdk-overlay-backdrop');
    this.backdrop.style.position = 'fixed';
    this.backdrop.style.top = '0';
    this.backdrop.style.left = '0';
    this.backdrop.style.width = '100%';
    this.backdrop.style.height = '100%';
    this.backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0.32)';
    this.backdrop.style.zIndex = '999'; // Just below dialog (dialog is usually 1000)
    this.backdrop.style.display = 'none';
    this.backdrop.style.pointerEvents = 'auto';

    // Add click listener
    this.backdrop.addEventListener('click', () => {
      if (this.chatOpened) {
        this.minimizeChat();
      }
    });

    document.body.appendChild(this.backdrop);
  }

  private showBackdrop(): void {
    if (this.backdrop) {
      this.backdrop.style.display = 'block';
    }
  }

  private hideBackdrop(): void {
    if (this.backdrop) {
      this.backdrop.style.display = 'none';
    }
  }
}
